// External deps
import { useEffect, useState, useCallback, useRef } from 'react';
import { Button, TextArea } from 'ypf-pas-general-ui-components';
import { FiSend } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import PropTypes from 'prop-types';

// Internal deps
import { sendMessage } from '../../adapters/requestServiceDetails/requestServiceDetailsAdapter';
import useToast from '../../hooks/useToast';
import useMessage from '../../hooks/useMessage';
import { formDateHour } from '../../utils/formatUtils';

import NoMessage from './NoMessages/NoMessage';
import './Message.scss';

const Message = ({ disabled, isMonthly }) => {
  const [message, setMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState(false);
  const { id } = useParams();

  const scrollableRootRef = useRef(null);
  const lastScrollDistanceToBottomRef = useRef(null);
  const { setNewToast } = useToast();
  const { loading, messages, hasNextPage, error, loadMore, refreshInitMessages } =
    useMessage(isMonthly);

  const [infiniteRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: loadMore,
    disabled: error,
    rootMargin: '450px 0px 0px 0px'
  });

  const sendTextMessage = () => {
    setLoadingMessage(true);
    sendMessage(id, `"${message}"`, isMonthly)
      .then(() => {
        setMessage('');
        refreshInitMessages();
        setLoadingMessage(false);
      })
      .catch((err) => {
        setLoadingMessage(false);
        setNewToast({
          type: 'ERROR',
          message: err.message
        });
      });
  };

  // We keep the scroll position when new items are added etc.
  useEffect(() => {
    const scrollableRoot = scrollableRootRef.current;
    const lastScrollDistanceToBottom = lastScrollDistanceToBottomRef.current ?? 0;

    if (scrollableRoot) {
      scrollableRoot.scrollTop = scrollableRoot.scrollHeight - lastScrollDistanceToBottom;
    }
  }, [messages]);

  const rootRefSetter = useCallback((node) => {
    scrollableRootRef.current = node;
  }, []);

  const handleRootScroll = useCallback(() => {
    const rootNode = scrollableRootRef.current;

    if (rootNode) {
      const scrollDistanceToBottom = rootNode.scrollHeight - rootNode.scrollTop;

      lastScrollDistanceToBottomRef.current = scrollDistanceToBottom;
    }
  }, []);

  const handleMessage = (e) => {
    e.preventDefault();
    setMessage(e.target.value);
  };

  const showDateHour = (value) => {
    const { newDate, newHour } = formDateHour(value);

    return `${newDate} | ${newHour}`;
  };

  return (
    <div>
      <div className="contenedor-chat" onScroll={handleRootScroll} ref={rootRefSetter}>
        <div className="contenedor-mensaje">
          {hasNextPage && (
            <div className="loading-message" ref={infiniteRef}>
              <span>Cargando mensajes...</span>
            </div>
          )}
          {messages.length ? (
            messages.map((item) => (
              <div
                className={`mensaje-contenedor-${item.esSolicitante ? 'emisor' : 'receptor'}`}
                key={item.fechaEnvio}
              >
                <div className="nombre-usuario">{item.usuario}</div>
                <div className="mensaje-space">{item.mensaje}</div>
                <div className="mensaje-hora">{showDateHour(item.fechaEnvio)}</div>
              </div>
            ))
          ) : (
            <NoMessage />
          )}
        </div>
      </div>
      {!disabled && (
        <>
          <hr />
          <div className="input-message">
            <TextArea
              className="message-text"
              disabled={loadingMessage}
              label=""
              maxLength={512}
              name="message"
              onChange={(e) => handleMessage(e)}
              placeholder="¡Escribí tu mensaje acá!"
              resize={false}
              rows={2}
              value={message}
            />
            <Button
              className="mx-2 mb-3"
              colorIcon="primary"
              disabled={!message || loadingMessage}
              icon={FiSend}
              onClick={() => sendTextMessage()}
              type="button"
              variant="light-gray"
              widthButton="50px"
            />
          </div>
        </>
      )}
    </div>
  );
};

Message.propTypes = {
  disabled: PropTypes.bool.isRequired,
  isMonthly: PropTypes.bool
};

Message.defaultProps = {
  isMonthly: false
};

export default Message;
